import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, MenuItem, Alert, CircularProgress, Grid } from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import ModuleHub from '../components/ModuleHub';
import { getFixedAssets, createFixedAsset, updateFixedAsset, deleteFixedAsset } from '../../../services/api';

const fmt = (v) => parseFloat(v || 0).toLocaleString('en-LK', { style: 'currency', currency: 'LKR' });

const FixedAssetsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ asset_code: '', name: '', category: '', purchase_date: '', purchase_cost: 0, useful_life_years: 5, depreciation_method: 'straight_line', serial_number: '' });

  useEffect(() => { loadData(); }, []);
  const loadData = async () => {
    try { setLoading(true); const r = await getFixedAssets(); setItems(r.data || []); }
    catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const calcDepreciation = (asset) => {
    const cost = parseFloat(asset.purchase_cost) - parseFloat(asset.salvage_value || 0);
    const years = asset.useful_life_years || 5;
    return asset.depreciation_method === 'straight_line' ? cost / years : cost * 0.2;
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, purchase_cost: parseFloat(form.purchase_cost), book_value: parseFloat(form.purchase_cost) };
      if (editItem) await updateFixedAsset(editItem.id, payload);
      else await createFixedAsset(payload);
      setDialogOpen(false); loadData();
    } catch (err) { setError(err.message); }
  };

  return (
    <ModuleHub moduleId="fixed-assets" showFeatures={false}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Button startIcon={<RefreshIcon />} onClick={loadData}>Refresh</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditItem(null); setForm({ asset_code: '', name: '', category: '', purchase_date: '', purchase_cost: 0, useful_life_years: 5, depreciation_method: 'straight_line', serial_number: '' }); setDialogOpen(true); }}>Add Asset</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}><Table>
          <TableHead><TableRow><TableCell>Code</TableCell><TableCell>Name</TableCell><TableCell>Category</TableCell><TableCell align="right">Cost</TableCell><TableCell align="right">Book Value</TableCell><TableCell align="right">Annual Dep.</TableCell><TableCell>Status</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
          <TableBody>{items.map(a => (
            <TableRow key={a.id}>
              <TableCell><strong>{a.asset_code}</strong></TableCell>
              <TableCell>{a.name}</TableCell>
              <TableCell>{a.category}</TableCell>
              <TableCell align="right">{fmt(a.purchase_cost)}</TableCell>
              <TableCell align="right">{fmt(a.book_value)}</TableCell>
              <TableCell align="right">{fmt(calcDepreciation(a))}</TableCell>
              <TableCell><Chip label={a.status} size="small" color={a.status === 'active' ? 'success' : 'default'} /></TableCell>
              <TableCell>
                <Button size="small" onClick={() => { setEditItem(a); setForm({ ...a }); setDialogOpen(true); }}>Edit</Button>
                <Button size="small" color="error" onClick={() => deleteFixedAsset(a.id).then(loadData)}>Delete</Button>
              </TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></TableContainer>
      )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem ? 'Edit Asset' : 'New Fixed Asset'}</DialogTitle>
        <DialogContent><Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={6}><TextField fullWidth label="Asset Code" value={form.asset_code} onChange={(e) => setForm({ ...form, asset_code: e.target.value })} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Serial Number" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} /></Grid>
          <Grid item xs={12}><TextField fullWidth label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Grid>
          <Grid item xs={6}><TextField fullWidth type="date" label="Purchase Date" InputLabelProps={{ shrink: true }} value={form.purchase_date || ''} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} /></Grid>
          <Grid item xs={6}><TextField fullWidth type="number" label="Purchase Cost" value={form.purchase_cost} onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })} /></Grid>
          <Grid item xs={6}><TextField fullWidth type="number" label="Useful Life (years)" value={form.useful_life_years} onChange={(e) => setForm({ ...form, useful_life_years: parseInt(e.target.value) })} /></Grid>
          <Grid item xs={12}><TextField fullWidth select label="Depreciation Method" value={form.depreciation_method} onChange={(e) => setForm({ ...form, depreciation_method: e.target.value })}>{['straight_line', 'declining_balance', 'units_of_production'].map(m => <MenuItem key={m} value={m}>{m.replace(/_/g, ' ')}</MenuItem>)}</TextField></Grid>
        </Grid></DialogContent>
        <DialogActions><Button onClick={() => setDialogOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleSave}>Save</Button></DialogActions>
      </Dialog>
    </ModuleHub>
  );
};

export default FixedAssetsPage;
